import React from "react";

import { FormField, FormSubscribe, RadioButtons, Text, usePyForm } from "@performyard/py-components";
import { Checkbox } from "@performyard/py-components/src/components/atoms/Checkbox";

//components
import Spacer from "../../../../../../../components/Spacer";

//api
import { useCompanySettings } from "../../../../../../../api/hooks";

import { ReviewQuestion } from "../../../utils";

//internals
import { EditQuestionBlockProps } from "../../QuestionBlock";
import { LegacyRatingScaleSection } from "./legacyRatingScale";
import { FormBuilderNumericInput } from "./numeric";

const RadialOptions = [
	{ label: "Do Not Score", value: "no" },
	{ label: "Score with Numeric Value", value: "numeric" },
	{ label: "Score with Rating Scale", value: "rating" },
];

const calculateRadioValue = (data: ReviewQuestion) => {
	if (data?.add_scoring) {
		return "numeric";
	}

	if (data?.add_scaling?.length) {
		return "rating";
	}

	return "no";
};

const GoalDiscussionEdit = ({ index }: EditQuestionBlockProps) => {
	const { data: settings } = useCompanySettings();
	const form = usePyForm();

	const decimalPlaces = settings?.decimal_places || 0;

	return (
		<FormSubscribe id="question-root" selector={(state) => [state.values]}>
			{([question]) => {
				const category = calculateRadioValue(question);
				return (
					<div>
						<div className="flex flex-col gap-2">
							<Text size="paragraph-3" weight="semi-bold">
								Score Goals
							</Text>
							<RadioButtons
								id="scoring-mode"
								options={RadialOptions}
								value={category}
								onChange={(value) => {
									if (value == "numeric") {
										form.setFieldValue("add_scoring", true);
										form.setFieldValue("add_scaling", undefined);
									} else if (value == "rating") {
										form.setFieldValue("add_scoring", false);
										form.setFieldValue("scoring_min", undefined);
										form.setFieldValue("scoring_max", undefined);
										form.setFieldValue("add_scaling", [{ value: "", name: "", description: "" }]);
									} else {
										form.setFieldValue("add_scoring", false);
										form.setFieldValue("scoring_min", undefined);
										form.setFieldValue("scoring_max", undefined);
										form.setFieldValue("add_scaling", undefined);
									}
								}}
							/>
						</div>

						{category !== "no" && (
							<div>
								<Spacer height={2} />
								<FormField<boolean>
									id="score_weights"
									label="Weighted Scoring"
									input={(field) => {
										return (
											<div className="flex items-center gap-2">
												<Checkbox
													id="score_weights"
													onCheckedChange={() => {
														field.onChange(!field.value);
													}}
													checked={field.value}
												/>
												<Text htmlFor="score_weights" size="paragraph-3">
													Allow scores to be weighted
												</Text>
											</div>
										);
									}}
								/>

								<Spacer height={2} />
							</div>
						)}

						{question?.add_scoring && (
							<div>
								<div className="flex flex-col gap-2">
									<FormSubscribe
										id="decimals"
										selector={(state) => [state.values.decimals, state.values.max]}
									>
										{([decimals]) => (
											<FormField<number | null>
												id="scoring_min"
												validators={{
													onChangeListenTo: ["scoring_max"],
													onChange: ({ fieldApi }) => {
														fieldApi.state.meta.errors = [];
														fieldApi.state.meta.isTouched = true;
													},
												}}
												label={
													<div className="min-w-[50px]">
														<Text
															size="caption"
															weight="regular"
															dangerouslySetClassName="text-caption font-regular"
														>
															Minimum
														</Text>
													</div>
												}
												labelPosition="left"
												input={(field) => {
													return (
														<FormBuilderNumericInput
															value={field.value}
															onChange={field.onChange}
															precision={decimals ? decimalPlaces : 0}
															placeholder="Minimum Value"
														/>
													);
												}}
											/>
										)}
									</FormSubscribe>
									<Spacer height={1} />

									<FormSubscribe
										id="decimals"
										selector={(state) => [state.values.decimals, state.values.min]}
									>
										{([decimals]) => (
											<FormField<number | null>
												id="scoring_max"
												label={
													<div className="min-w-[50px]">
														<Text
															size="caption"
															weight="regular"
															dangerouslySetClassName="text-caption font-regular"
														>
															Maximum
														</Text>
													</div>
												}
												labelPosition="left"
												input={(field) => {
													return (
														<FormBuilderNumericInput
															value={field.value}
															onChange={field.onChange}
															precision={decimals ? decimalPlaces : 0}
															placeholder="Maximum Value"
														/>
													);
												}}
											/>
										)}
									</FormSubscribe>
								</div>
							</div>
						)}
						{question?.add_scaling?.length ? (
							<LegacyRatingScaleSection index={index} fieldKey="add_scaling" />
						) : null}
					</div>
				);
			}}
		</FormSubscribe>
	);
};

export default GoalDiscussionEdit;
